/* =======================================================================
   Global Variables and Element References
======================================================================== */

/**
 * Global counter to assign unique IDs to the "Show more" buttons.
 */
let showMoreBtnCounter = 1;

/**
 * Stores the discount brackets extracted from the discount table.
 */
let discountBrackets = [];

/**
 * Variable to track whether to include VAT
 */
let includeVAT = true;

/**
 * Retrieve the current market VAT rate.
 * If the input is "21", it will be converted to 0.21 by dividing by 100.
 * If the input is "21.5", it becomes 0.215, which is 21.5%.
 */
const vatRateInput = document.getElementById('current-market-vat-rate');
let vatRate = 0;

if (vatRateInput) {
  const rawVATValue = parseFloat(vatRateInput.value);
  if (!isNaN(rawVATValue)) {
    // Convert from "21" or "21.5" to "0.21" or "0.215"
    vatRate = rawVATValue / 100;
  }
}

/** 
 * Get the Market ID from the hidden input on the page
 */
const marketId_element = document.getElementById('store-market-id');
const marketId = marketId_element.value;

/** 
 * Get the ISO code from the hidden input on the page
 */
const iso_code_element = document.getElementById('store-iso-code');
const iso_code = iso_code_element ? iso_code_element.value : 'en'; // Default to 'en' if not found

/** 
 * Get the currency ISO code from the hidden input on the page
 */
const currency_iso_code_element = document.getElementById('store-currency-iso-code');
const currency_iso_code = currency_iso_code_element ? currency_iso_code_element.value : 'EUR'; // Default to 'EUR' if not found

/** 
 * Get the Product ID from the hidden input on the page
 */
const productId = document.querySelector('input[name="product-id"]').value;

/** 
 * Global array to store excluded variant IDs
 */
let excludedVariantIds = [];

/**
 * Global currency symbol, detected from original prices
 */
let currencySymbol = '';

/**
 * Indicates whether the currency comes before or after the price. Values: ‘before’ | ‘after’
 */
let currencySymbolPlacement = 'after';
let detectedDecimalSeparator  = '.';   // "."  or ","
let detectedThousandSeparator = ',';   // ","  or "."
let separatorsLocked = false;

/**
 * Declare original single unit and compare prices
 */
let originalSingleUnitPriceWithVAT = 0;
let originalComparePriceWithVAT = 0;

/**
 * Flag indicating whether the discount percentage column exists.
 * Assuming it always exists in this implementation.
 */
let discountPercentageColumnExists = true;

/**
 * Declare original print system iframe url src empty
 */
let printSystemIframeUrlSrc = "";

/**
 * Store variant metafields json object
 */
const VARIANT_META = (() => {
  try {
    const el = document.getElementById('variant-metafields-json');
    return el ? JSON.parse(el.textContent) : {};
  } catch (e) {
    console.error('Failed to parse variant-metafields-json:', e);
    return {};
  }
})();

/**
 * DOM Element references used throughout the script.
 */
const personaliseProductIframe = document.getElementById('personalise-product-iframe');
const printSystemIframeUrlInput = document.getElementById('print-system-iframe-url');
const optionSelectors = document.querySelectorAll('.option-selector');
const discountTableElement = document.querySelector('.shopacado-discount-table');
const productSkuElement = document.querySelector('.product-sku');
const personaliseProductBtn = document.querySelector('#personalise-product-btn');
const quantityInput = document.querySelector('.qty-input__input');

/* =======================================================================
   Initialization Functions
======================================================================== */

/**
 * This code is executed once the DOM content has fully loaded. It ensures that
 * various initialization tasks are performed, including setting up prices, 
 * custom tabs, variant change handling, VAT toggle, and limiting variant options.
 */
document.addEventListener('DOMContentLoaded', async function() {

  // 1) Fetch excluded variants for that product in current market if product has more than 1 variant
  if(optionSelectors.length != 0){
    await fetchExcludedVariantIds();
  } 
  
  // 2) Initialize original product prices
  initializeOriginalPrices();
  
  // 3) Reorder measure-like fieldsets
  autoReorderMeasureFieldsets();

  // 4) Custom tabs
  initCustomTabs();

  // 5) Quantity input changes
  initQuantityInputChangeHandler();

  // 6) Toggle VAT inclusion
  initializeVATToggle();

  // 7) Variant change handler
  initVariantChangeHandler();

  // 8) Large volume quote SKU fill
  initLargeVolumeQuoteSkuFill();
});

/**
 * Initialize the original prices (price_current and price_was) with VAT
 * so we can compute exVAT if needed.
 */
function initializeOriginalPrices() {
  const priceCurrentElement = document.querySelector('.price__current');

  if (priceCurrentElement) {
    const priceText = priceCurrentElement.querySelector('.js-value').textContent.trim();

    // Detect currency symbol (leave letters like "Ft")
    const symbolMatch = priceText.replace(/[0-9.,\s\u00A0]/g, '');
    currencySymbol = symbolMatch || '';

    // Placement
    currencySymbolPlacement = determineCurrencySymbolPlacement(priceText, currencySymbol);

    // IMPORTANT: theme shows EX-VAT by default → parse as exVAT
    const parsedExVAT = parseUnitPrice(priceText) || 0;

    // Persist both bases for later use
    window.originalSingleUnitPriceExVAT   = parsedExVAT;
    originalSingleUnitPriceWithVAT        = vatRate > 0 ? parsedExVAT * (1 + vatRate) : parsedExVAT;
  } else {
    // Fallbacks
    window.originalSingleUnitPriceExVAT = 0;
    originalSingleUnitPriceWithVAT      = 0;
    currencySymbol = '';
  }

  // Compare/was price (theme default is also ex-VAT in setup)
  const priceWasElement = document.querySelector('.price__was');
  if (priceWasElement) {
    const wasText = priceWasElement.textContent.trim();
    const wasExVAT = parseUnitPrice(wasText) || 0;
    // Store compare as WITH VAT so the rest of the code can reuse it consistently
    originalComparePriceWithVAT = vatRate > 0 ? wasExVAT * (1 + vatRate) : wasExVAT;
  } else {
    originalComparePriceWithVAT = 0;
  }
}

/**
 * Function to fetch excluded variant IDs of the product by current market
 */
async function fetchExcludedVariantIds() {
  try {
    const response = await fetch('https://api-shopify.enveseur.com/shopify-product/excluded-market-variants/product/' + productId + '/market/' + marketId);
    excludedVariantIds = await response.json();
  } catch (error) {
    console.error("Error fetching excluded variant IDs:", error);
  }
}

/* =======================================================================
   Variant Handling Functions
======================================================================== */

/**
 * Retrieves the selected variant based on the selected options.
 * @param {NodeList} optionSelectors - The list of option selector elements.
 * @returns {Object|null} The selected variant object or null if not found.
 */
function getSelectedVariant(optionSelectors) {
  const productJsonElement = document.querySelector('variant-picker script[type="application/json"]');

  if (productJsonElement) {
    let productData = null;
    try {
      productData = JSON.parse(productJsonElement.textContent);
    } catch (e) {
      return null;
    }

    const selectedOptions = Array.from(optionSelectors).map((fieldset) => {
      const checkedInput = fieldset.querySelector('input:checked');
      return checkedInput ? checkedInput.value : null;
    });

    // If no options are selected, return the first available variant
    if (selectedOptions.includes(null)) {
      return productData.product.variants[0] || null;
    }

    // Find the variant that matches the selected options
    return productData.product.variants.find(variant => {
      return variant.options.every((option, index) => option === selectedOptions[index]);
    });
  }else {
    // No productJsonElement found, likely a single product without multiple variants.
    // Retrieve the variant SKU from the span '.product-variant_sku__value'
    // Retrieve the variant ID from the hidden input '#product-variant_id__value'
    const skuElement = document.querySelector('.product-sku__value');
    const singleVariantSkuValue = skuElement.textContent;
    const singleVariantIdInput = document.getElementById('product-variant_id__value');
    if (singleVariantSkuValue && singleVariantIdInput) {
      const variantSku = singleVariantSkuValue;
      const variantId = singleVariantIdInput.value;
      
      // Return a minimal variant object containing the SKU and the ID.
      // If additional properties are needed, they can be included here.
      return {
        sku: variantSku,
        id: variantId
      };
    }
  }
  
  return null;
}

/**
 * Updates UI elements when a valid variant is selected.
 */
function updateUIForValidVariant() {
  discountTableElement?.classList.remove('blurred');
  personaliseProductBtn?.classList.remove('disabled');
}

/**
 * Updates UI elements when no valid variant is selected.
 */
function updateUIForInvalidVariant() {
  discountTableElement?.classList.add('blurred');
  personaliseProductBtn?.classList.add('disabled');

  // Clear discount brackets and remove any highlighting as the variant is invalid
  discountBrackets = [];
  highlightCurrentPriceBreak(null);
}

/**
 * Handles the variant change event.
 */
async function handleVariantChange() {
  // Hide out-of-stock message on variant change
  const oosMsg = document.getElementById('product-out-of-stock-msg');
  if (oosMsg && !oosMsg.hidden) {
    oosMsg.hidden = true;
    oosMsg.classList.add('hidden');
    oosMsg.style.display = 'none';
    oosMsg.setAttribute('aria-hidden', 'true');
  }

  // Retrieve the currently selected variant
  const variant = getSelectedVariant(optionSelectors);

  if (variant && variant.sku) {
    // If the variant is valid, update UI to reflect a valid selection
    productSkuElement?.classList.remove('blurred');
    const productMedia = document.querySelectorAll('.media-gallery');
    //productMedia?.forEach(container => container.classList.remove('blurred'));
    // Remove blur from the product metafields containers
    const metafieldsContainers = document.querySelectorAll('.product-metafields-container');
    metafieldsContainers.forEach(container => container.classList.remove('blurred'));

    // Fetch metafields for the currently selected variant and display them in the containers
    if (variant.id && optionSelectors.length !== 0) {
      const variantData = getVariantFromLocalMap(variant.id);
      if (variantData) {
        displayVariantMetafieldsForContainers(variantData);
      } else {
        // Hide all rows if no data found
        document.querySelectorAll('.product-metafields-container tr')
          .forEach(row => row.classList.add('custom-hidden'));
      }
    }

    // If the variant is not available in the market, clear the iframe and update UI.
    if (optionSelectors.length != 0) {
      if (excludedVariantIds.includes(String(variant.id))) {
        clearPersonaliseProductIframeSrc();
        updateUIForInvalidVariant();

        setTimeout(() => {
          // Disable the add-to-cart button and update its text.
          const addToCartButton = document.querySelector('.product-info__add-button button[name="add"]');
          if (addToCartButton) {
            addToCartButton.disabled = true;
            addToCartButton.textContent = 'Unavailable';
          }
          // Hide the element with class "price__default"
          const priceDefault = document.querySelector('.price__default');
          if (priceDefault) {
            priceDefault.setAttribute('hidden', '');
          }
          // Show the element with class "price__no-variant" by removing the hidden attribute.
          const priceNoVariant = document.querySelector('.price__no-variant');
          if (priceNoVariant) {
            priceNoVariant.removeAttribute('hidden');
          }
          // Add the class "blurred" to the discount table.
          if (discountTableElement) {
            discountTableElement.classList.add('blurred');
          }
        }, 50);

        return; // Stop further processing.
      }
    }

    // Update the iframe source to reflect the selected variant's SKU
    updatePersonaliseProductIframeSrc(variant.sku);

    // Update UI for a valid variant selection
    updateUIForValidVariant();

    // Reset the quantity input to 1 for the new variant
    if (quantityInput) {
      quantityInput.value = 1;
    }

    // Clear existing discount brackets to ensure fresh data for the new variant
    discountBrackets = [];

    // Use a short delay to ensure the DOM updates are complete before proceeding
    setTimeout(async () => {
      // Re-initialize original prices after switching to the new variant
      initializeOriginalPrices();

      // Wait until discount brackets are ready, then update table prices and the displayed price
      initDiscountBracketsWhenTableIsReady(async () => {
        updateDiscountTablePricesWithVAT();
        updatePrice(); // Recalculate prices after variant change

        // Highlight the discount bracket corresponding to quantity 1 (default quantity)
        if (quantityInput) {
          highlightCurrentPriceBreak(parseInt(quantityInput.value) || 1);
        }
      });
    }, 50); // Adjust the delay if necessary to ensure all elements are ready

  } else {
    // If the variant is invalid (no SKU or not found), update UI to reflect an invalid selection
    clearPersonaliseProductIframeSrc();
    updateUIForInvalidVariant();
    productSkuElement?.classList.add('blurred');
    const productMedia = document.querySelectorAll('.media-gallery');
    //productMedia?.forEach(container => container.classList.add('blurred'));
    // Blur the product metafields containers
    const metafieldsContainers = document.querySelectorAll('.product-metafields-container');
    metafieldsContainers.forEach(container => container.classList.add('blurred'));
  }
}

/** Helper: lookup by id (stringify to match JSON keys) */
function getVariantFromLocalMap(variantId) {
  const key = String(variantId);
  return VARIANT_META[key] || null;
}

/**
 * Initializes the variant change handler by adding event listeners.
 */
function initVariantChangeHandler() {
  // Add event listeners to variant inputs
  optionSelectors.forEach(function(fieldset) {
    const inputs = fieldset.querySelectorAll('input');
    inputs.forEach(function(input) {
      input.addEventListener('change', handleVariantChange);
    });
  });

  // Initialize on page load
  handleVariantChange();
}

/* =======================================================================
   Discount Table
======================================================================== */

/** When the discount table exists, build `discountBrackets`, wire clicks, then run callback. */
function initDiscountBracketsWhenTableIsReady(callback) {
  const table = document.querySelector('.shopacado-discount-table');
  if (table) {
    replaceDiscountTableHeaders();
    getDiscountBrackets();
    initRowClickToSetQuantity();
    callback?.();
  } else {
    const observer = new MutationObserver(() => {
      const t = document.querySelector('.shopacado-discount-table');
      if (!t) return;
      observer.disconnect();
      replaceDiscountTableHeaders();
      getDiscountBrackets();
      initRowClickToSetQuantity();
      callback?.();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }
}

/** Replace table header labels from hidden inputs. */
function replaceDiscountTableHeaders() {
  const qtyLabel   = document.getElementById('product-quantity-discount-table_quantity_label')?.value.trim() || 'Quantity';
  const priceLabel = document.getElementById('product-quantity-discount-table_rice-per-unit_label')?.value.trim() || 'Price per unit';
  const ths = document.querySelectorAll('.shopacado-discount-table thead th');
  if (ths.length >= 2) { ths[0].textContent = qtyLabel; ths[1].textContent = priceLabel; }
}

/** Build `discountBrackets` from table rows. Uses `parseUnitPrice` for price parsing. */
function getDiscountBrackets() {
  discountBrackets = [];
  const table = document.querySelector('.shopacado-discount-table');
  if (!table) return;

  table.querySelectorAll('tbody tr').forEach(row => {
    const cells = row.querySelectorAll('td');
    if (cells.length < 2) return;

    const qtyText  = cells[0].textContent.trim();
    const priceTxt = cells[1].querySelector('.money')?.textContent.trim() || "0";
    const { min, max } = parseQuantityRange(qtyText);
    const originalFinalPriceWithVAT = parseUnitPrice(priceTxt);

    discountBrackets.push({ min, max, originalFinalPriceWithVAT, rowElement: row });
  });

  discountBrackets.sort((a, b) => a.min - b.min);
}

/** Return the bracket for a quantity (or null). */
function getDiscountBracket(quantity) {
  return discountBrackets.find(b => quantity >= b.min && (quantity <= b.max || b.max === Infinity)) || null;
}

/** Highlight the row matching the current quantity. */
function highlightCurrentPriceBreak(quantity) {
  document.querySelectorAll('.shopacado-discount-table tbody tr')
    .forEach(r => r.classList.remove('current-price-break'));

  if (quantity == null || quantity <= 0 || !discountBrackets.length) return;

  const b = getDiscountBracket(quantity);
  if (b) b.rowElement.classList.add('current-price-break');
}

/** Parse "range" text into {min,max}. Supports "5-10", "10000+", "12". */
function parseQuantityRange(rangeText) {
  let min = 0, max = Infinity;
  if (rangeText.includes('+')) {
    min = parseInt(rangeText.replace('+', '').trim(), 10);
  } else if (rangeText.includes('-')) {
    const [a, b] = rangeText.split('-').map(s => s.trim());
    min = parseInt(a, 10); max = parseInt(b, 10);
  } else {
    min = parseInt(rangeText.trim(), 10); max = min;
  }
  return { min, max };
}

/** Make rows set quantity to bracket.min (idempotent wiring). */
function initRowClickToSetQuantity() {
  if (!discountBrackets.length || !quantityInput) return;

  discountBrackets.forEach(b => {
    const row = b.rowElement;
    if (!row || row.dataset.listenerAdded) return;
    row.style.cursor = 'pointer';
    row.addEventListener('click', () => {
      quantityInput.value = b.min;
      quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
    });
    row.dataset.listenerAdded = 'true';
  });
}

/* =======================================================================
   Price Update Functions
======================================================================== */

/**
 * Update discount-table unit prices:
 * - Read discount % per row
 * - Apply to original unit price (+ VAT if enabled)
 * - Whole-amount markets: show integer via .50 up rule
 * - Others: keep 4 decimals; then grey the last 2 via rewriteQtyPricesToMultipleDecimals()
 */
function updateDiscountTablePricesWithVAT() {
  const table = document.querySelector('.shopacado-discount-table');
  if (!table) return;

  const mId = String(marketId);
  const rows = table.querySelectorAll('tbody tr');

  const baseUnitExVAT = (typeof window.originalSingleUnitPriceExVAT === 'number' && !isNaN(window.originalSingleUnitPriceExVAT))
    ? window.originalSingleUnitPriceExVAT
    : 0;

  rows.forEach(row => {
    const cells = row.querySelectorAll('td');
    if (cells.length < 2) return;

    // 1) Read qty range and discount %
    const qtyText = cells[0].textContent.trim();
    const { min: qtyMin } = parseQuantityRange(qtyText);
    const discountAttr = cells[1].getAttribute('data-shopacado-discount-val');
    const discountPct = parseFloat(discountAttr) || 0;

    // 2) EX-VAT unit with discount (precise), then Shopify per-unit cents rounding (2dp)
    const unitExVAT_raw = baseUnitExVAT * (1 - discountPct / 100);
    const unitExVAT_2dp = roundHalfUp(unitExVAT_raw, 2);

    // 3) Subtotal EX-VAT for this row (using bracket min qty)
    let subtotalExVAT = unitExVAT_2dp * qtyMin;

    // 4) Apply market cents policy at subtotal level
    subtotalExVAT = applyCentsPolicy(subtotalExVAT, mId);

    // 5) VAT (if enabled) computed on EX-VAT subtotal
    const rVAT = (typeof vatRate === 'number' && !isNaN(vatRate)) ? vatRate : 0;
    let totalRow;
    if (includeVAT) {
      const vatAmount = roundHalfUp(subtotalExVAT * rVAT, 2);
      totalRow = subtotalExVAT + vatAmount;
    } else {
      totalRow = subtotalExVAT;
    }

    // 6) Effective per-unit for display = row total / qty
    const effectiveUnit = qtyMin > 0 ? (totalRow / qtyMin) : 0;

    // 7) Whole-amount markets: show integer unit (rare, but keep your rule)
    if (isWholeAmountMarket(mId)) {
      const intPrice = roundHalfUpToInteger(effectiveUnit);
      cells[1].innerHTML = `<span class="money">${intPrice}</span>`;
      return;
    }

    // 8) Show 4 decimals; you'll grey the last two in rewriteQtyPricesToMultipleDecimals()
    const numericOnly = effectiveUnit.toFixed(4);
    cells[1].innerHTML = `<span class="money">${numericOnly}</span>`;
  });

  rewriteQtyPricesToMultipleDecimals();
}

/**
 * Format all .money spans to:
 * - Keep 4 decimals
 * - Use detected decimal separator
 * - Grey last 2 decimal digits
 * - Place currency symbol per placement setting
 * - Whole-amount markets: show 0 decimals
 */
function rewriteQtyPricesToMultipleDecimals() {
  const table = document.querySelector('.shopacado-discount-table');
  if (!table) return;

  const moneyElements = table.querySelectorAll('tbody tr td .money');
  if (!moneyElements.length) return;

  const mId = String(marketId);

  if (isWholeAmountMarket(mId)) {
    moneyElements.forEach(moneyEl => {
      const raw = parseFloat(moneyEl.textContent.trim()) || 0;
      const whole = roundHalfUpToInteger(raw);
      moneyEl.innerHTML = formatPrice(whole, 0);
    });
    return;
  }

  const decimalSeparator = detectedDecimalSeparator;

  moneyElements.forEach(moneyEl => {
    let numericText = moneyEl.textContent.trim(); // e.g. "0.3700"
    let [intPart, fracPart] = numericText.split('.');
    if (!intPart || !fracPart) return;

    fracPart = fracPart.padEnd(4, '0');
    const mainFraction = fracPart.slice(0, 2);
    const lastTwo = fracPart.slice(2);

    const numberWithSeparator = intPart + decimalSeparator + mainFraction +
      `<span style="color:#888">${lastTwo}</span>`;

    moneyEl.innerHTML = (currencySymbolPlacement === 'before')
      ? `${currencySymbol}&nbsp;${numberWithSeparator}`
      : `${numberWithSeparator}${currencySymbol}`;
  });
}

/**
 * Recalculate product price:
 * - Apply active discount bracket (floored to 2-decimal factor)
 * - Pre-VAT cents rule by market (ceil/trunc/round)
 * - Apply/remove VAT (trunc to 2 decimals)
 * - Multiply by quantity
 * - Whole-amount markets: integer with .50 up
 * - Write current and compare (if present)
 */

// Markets that use whole-number totals with .50+ up, <.50 down.
const WHOLE_AMOUNT_MARKETS = new Set([
  '31489491008', // Hungary
]);

// Markets with custom pre-VAT cents behavior
const CEIL_TO_CENTS_MARKETS  = [
  '31056592960', // India
];
const TRUNC_TO_CENTS_MARKETS = [
  '31027789888', // Hong Kong
];

function updatePrice() {
  const priceCurrent = document.querySelector('.price__current');
  if (!priceCurrent) return;

  const quantity = Math.max(parseInt(quantityInput?.value || "1", 10), 1);
  const mId = String(marketId);

  // Active discount %
  let discountPercentage = 0;
  const bracket = getDiscountBracket(quantity);
  if (bracket) {
    const cell = bracket.rowElement.querySelector('td[data-shopacado-discount-val]');
    discountPercentage = parseFloat(cell?.getAttribute('data-shopacado-discount-val') || "0");
  }

  // ---- Source of truth: EX-VAT base unit ----
  const baseExVAT = (typeof window.originalSingleUnitPriceExVAT === 'number' && !isNaN(window.originalSingleUnitPriceExVAT))
    ? window.originalSingleUnitPriceExVAT
    : 0;

  // 1) Discount EX-VAT unit precisely, then Shopify per-unit cents rounding (2dp)
  const discountedUnitExVAT = baseExVAT * (1 - discountPercentage / 100);
  const perUnitExVAT2 = roundHalfUp(discountedUnitExVAT, 2);   // ★ per-unit 2dp

  // 2) Subtotal EX-VAT
  let subtotalExVAT = perUnitExVAT2 * quantity;

  // Apply market cents policy (still EX-VAT subtotal)
  subtotalExVAT = applyCentsPolicy(subtotalExVAT, mId);

  // 3) VAT computed on EX-VAT subtotal (NOT per unit with VAT)
  const rVAT = (typeof vatRate === 'number' && !isNaN(vatRate)) ? vatRate : 0;
  let total;
  if (includeVAT) {
    const vatAmount = roundHalfUp(subtotalExVAT * rVAT, 2);
    total = subtotalExVAT + vatAmount;
  } else {
    total = subtotalExVAT;
  }

  // 4) Display
  if (isWholeAmountMarket(mId)) {
    priceCurrent.querySelector('.js-value').textContent = formatPrice(roundHalfUpToInteger(total), 0);
  } else {
    priceCurrent.querySelector('.js-value').textContent = formatPrice(total, 2);
  }

  // ---- Compare/Was with same pipeline ----
  const priceWas = document.querySelector('.price__was');
  if (priceWas && originalComparePriceWithVAT > 0) {
    const compareWithVAT = Number(originalComparePriceWithVAT) || 0;
    const baseCompareExVAT = rVAT > 0 ? (compareWithVAT / (1 + rVAT)) : compareWithVAT;

    // If "was" should reflect the active bracket, keep the factor; else set factor=1
    const compareUnitExVATRaw = baseCompareExVAT * (1 - discountPercentage / 100);
    const compareUnitExVAT2   = roundHalfUp(compareUnitExVATRaw, 2);
    let compareSubtotalExVAT  = compareUnitExVAT2 * quantity;
    compareSubtotalExVAT      = applyCentsPolicy(compareSubtotalExVAT, mId);

    let compareTotal;
    if (includeVAT) {
      const compareVAT = roundHalfUp(compareSubtotalExVAT * rVAT, 2);
      compareTotal = compareSubtotalExVAT + compareVAT;
    } else {
      compareTotal = compareSubtotalExVAT;
    }

    if (isWholeAmountMarket(mId)) {
      priceWas.querySelector('.js-value').textContent = formatPrice(roundHalfUpToInteger(compareTotal), 0);
    } else {
      priceWas.querySelector('.js-value').textContent = formatPrice(compareTotal, 2);
    }
  }
}

/* =======================================================================
   Price Parsing/Formatting Helper Functions
======================================================================== */

/** Parse first price-like token; detect & lock decimal/thousand separators. */
function parseUnitPrice(priceText) {
  if (!priceText) return 0;

  // 1) Capture digits + (., space, NBSP) groups
  const m = priceText.match(/\d(?:[\d.,\s\u00A0])*/);
  if (!m) return 0;

  // 2) Normalize spaces/NBSP -> remove
  let t = m[0].replace(/\u00A0/g, ' ').replace(/ /g, '');

  // 3) If it looks like 1.234 or 1.234.567 (pure thousand groups), strip [.,] → integer
  if (/^\d{1,3}(?:[.,]\d{3})+$/.test(t)) {
    return parseFloat(t.replace(/[.,]/g, '')) || 0;
  }

  // 4) Otherwise decide whether last separator is decimal:
  const lastDot = t.lastIndexOf('.');
  const lastCom = t.lastIndexOf(',');
  const lastSep = Math.max(lastDot, lastCom);

  if (lastSep !== -1) {
    const fracLen = t.length - lastSep - 1;
    if (fracLen === 1 || fracLen === 2) {
      // Treat last separator as decimal; other same separators before it are thousands
      const dec = t[lastSep];
      const thou = dec === '.' ? ',' : '.';
      t = t
        .replace(new RegExp('\\' + thou, 'g'), '') // strip thousand sep
        .replace(dec, '.');                         // set decimal point
      return parseFloat(t) || 0;
    } else {
      // Not a 1–2 digit fraction → treat separators as thousands
      return parseFloat(t.replace(/[.,]/g, '')) || 0;
    }
  }

  // 5) Plain integer like "1075"
  return parseFloat(t) || 0;
}

/** Parse "- X%" from text; returns number or 0. */
function parseDiscountPercentage(s) {
  const m = s.match(/-\s*(\d+(?:[.,]\d+)?)%/);
  return m ? parseFloat(m[1].replace(',', '.')) || 0 : 0;
}

/** Format price with detected separators and layout. */
function formatPrice(price, decimals = 2) {
  let [intPart, fracPart = ''] = price.toFixed(decimals).split('.');
  intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, detectedThousandSeparator);
  const n = decimals ? intPart + detectedDecimalSeparator + fracPart : intPart;
  return currencySymbolPlacement === 'before' ? `${currencySymbol} ${n}` : `${n}${currencySymbol}`;
}

/** Detect if symbol sits before or after digits. */
function determineCurrencySymbolPlacement(text, symbol) {
  const di = text.search(/\d/), si = text.indexOf(symbol);
  return (si !== -1 && si < di) ? 'before' : 'after';
}

/** Whole-number market with .50 up rule. */
function isWholeAmountMarket(id) { return WHOLE_AMOUNT_MARKETS.has(String(id)); }

function roundHalfUp(n, decimals = 0) {
  const f = Math.pow(10, decimals);
  // For positives, Math.floor(x + 0.5) is half-up; negatives behave correctly too.
  return Math.floor(n * f + 0.5) / f;
}

/** Round half up to integer. */
function roundHalfUpToInteger(n) {
  return roundHalfUp(n, 0);
}

// Apply market cents policy to a raw number at 2 decimals
function applyCentsPolicy(n, mId) {
  if (CEIL_TO_CENTS_MARKETS.includes(mId))  return Math.ceil(n * 100) / 100;
  if (TRUNC_TO_CENTS_MARKETS.includes(mId)) return Math.floor(n * 100) / 100;
  return roundHalfUp(n, 2); // default half-up on ties
}

/* =======================================================================
   Reorder Measure Options
======================================================================== */

/** Parse "W x H" (commas/dots, optional "mm"). Returns {width,height,first,second,minSide,maxSide,area} or null. */
function parseWidthHeight(text = '') {
  const m = /(\d+(?:[.,]\d+)?)\s*[x×]\s*(\d+(?:[.,]\d+)?)/i.exec(text);
  if (!m) return null;
  const w = parseFloat(m[1].replace(',', '.'));
  const h = parseFloat(m[2].replace(',', '.'));
  return { width: w, height: h, first: w, second: h, minSide: Math.min(w, h), maxSide: Math.max(w, h), area: w * h };
}

/** Auto-sort radio options that look like measures: parsed first, then by first->second number, else by label text. */
function autoReorderMeasureFieldsets() {
  const fieldsets = document.querySelectorAll('fieldset.option-selector');
  const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

  Array.from(fieldsets)
    .filter(fs => !fs.closest('.quick-add-drawer')) // exclude quick-add
    .forEach(fs => {
      const container = fs.querySelector('.option-selector__btns');
      if (!container) return;

      const radios = container.querySelectorAll('input[type="radio"].opt-btn');
      if (!radios.length) return;

      let parsedCount = 0;
      const items = Array.from(radios).map(input => {
        const label = container.querySelector(`label[for="${input.id}"]`);
        const text = (label?.textContent || input.value || '').trim();
        const parsed = parseWidthHeight(input.value) || parseWidthHeight(text);
        if (parsed) parsedCount++;
        return { input, label, text, parsed };
      });

      if (parsedCount < items.length / 2) return; // mostly not measures → skip

      items.sort((a, b) => {
        const pa = a.parsed, pb = b.parsed;
        if (pa && !pb) return -1;
        if (!pa && pb) return 1;
        if (pa && pb) {
          if (pa.first !== pb.first)   return pa.first - pb.first;
          if (pa.second !== pb.second) return pa.second - pb.second;
        }
        return collator.compare(a.text, b.text);
      });

      const frag = document.createDocumentFragment();
      items.forEach(({ input, label }) => { if (input) frag.appendChild(input); if (label) frag.appendChild(label); });
      container.appendChild(frag);
    });
}

/* =======================================================================
   VAT Toggle
======================================================================== */

/** Sync checkbox ↔ cookie ↔ UI; default VAT on. */
function initializeVATToggle() {
  const el = document.getElementById('show-vat-checkbox');
  if (!el) return;

  const c = getCookie('includeVAT');
  includeVAT = c === null ? true : c === 'true';
  setCookie('includeVAT', includeVAT, 30);
  el.checked = includeVAT;

  setVATSuperscripts(includeVAT);

  el.addEventListener('change', () => {
    includeVAT = el.checked;
    setCookie('includeVAT', includeVAT, 30);

    setVATSuperscripts(includeVAT);

    updateDiscountTablePricesWithVAT();
    updatePrice();
  });
}

/** Show INC VAT when includeVAT=true; show EX VAT when includeVAT=false. */
function setVATSuperscripts(includeVAT) {
  const vatRate = parseFloat(document.getElementById('current-market-vat-rate')?.value || '0');
  const showVatLabels = vatRate > 0; // hide both if VAT is 0

  const exNodes = document.querySelectorAll('.product-info__ex-vat_sup');
  const incNodes = document.querySelectorAll('.product-info__inc-vat_sup');

  exNodes.forEach(n => n.classList.toggle('custom-hidden', includeVAT || !showVatLabels));
  incNodes.forEach(n => n.classList.toggle('custom-hidden', !includeVAT || !showVatLabels));
}

/* =======================================================================
   Quantity Handling Functions
======================================================================== */

/**
 * Initializes the quantity input change handler.
 */
function initQuantityInputChangeHandler() {
  if (!quantityInput) {
    return;
  }

  // Initialize discount brackets when the table is ready
  initDiscountBracketsWhenTableIsReady(function() {
    const initialQuantity = parseInt(quantityInput.value);
    if (!isNaN(initialQuantity)) {
      highlightCurrentPriceBreak(initialQuantity);
    }
  });

  // Add event listener to quantity input
  quantityInput.addEventListener('input', function() {
    const quantity = parseInt(quantityInput.value);

    if (isNaN(quantity)) {
      highlightCurrentPriceBreak(null);
    } else {
      highlightCurrentPriceBreak(quantity);
    }

    // After highlighting the correct bracket row, update the main price
    updatePrice();
  });
}

/* =======================================================================
   Personalise Product Functions
======================================================================== */

// Single "personalise" button:
const personaliseButton = document.querySelector('#personalise-product-btn');
if (personaliseButton) {
  personaliseButton.addEventListener('click', () => {
    // Each time the user clicks, we set the iframe src
    // to the latest lazyIframeUrl (updated by variant changes).
    personaliseProductIframe.src = printSystemIframeUrlSrc;

    // Then open the modal
    window.location.hash = '#personalise-product-modal';
  });
}

// Close btn
const closeBtn = document.querySelector('#personalise-product-modal .close');
if (closeBtn) {
  closeBtn.addEventListener('click', () => {
    // Change the hash to close the modal (this will remove the :target state)
    window.location.hash = '';
  });
}

/**
 * Updates the iframe source with the provided SKU.
 * @param {string} sku - The SKU of the selected variant.
 */
function updatePersonaliseProductIframeSrc(sku) {
  if(printSystemIframeUrlInput){
    printSystemIframeUrlSrc = printSystemIframeUrlInput.value + sku;
  }
}

/**
 * Clears the iframe source.
 */
function clearPersonaliseProductIframeSrc() {
  if(personaliseProductIframe){
    personaliseProductIframe.src = '';
  } 
}

/* =======================================================================
   Dispatch countdown widget
======================================================================== */

(function() {
  const widget = document.getElementById('custom-dispatch-countdown-widget');
  if (!widget) return;

  const today = new Date();
  const dow   = today.getDay();
  const mm    = String(today.getMonth() + 1).padStart(2, '0');
  const dd    = String(today.getDate()).padStart(2, '0');
  const todayKey = `${mm}-${dd}`;

  // 1) Weekend? hide.
  if (dow === 0 || dow === 6) {
    widget.style.display = 'none';
    return;
  }

  // 2) Spanish national bank holidays (MM-DD).
  const holidays = [
    '01-01', // New Year's Day
    '01-06', // Epiphany
    '05-01', // Labor Day
    '08-15', // Assumption
    '09-11', // Catalonia Day
    '10-12', // National Day
    '11-01', // All Saints
    '12-06', // Constitution Day
    '12-08', // Immaculate Conception
    '12-25'  // Christmas
  ];
  if (holidays.includes(todayKey)) {
    widget.style.display = 'none';
    return;
  }
  
  const hrsSpan  = document.getElementById('custom-dispatch-countdown-hours-span');
  const minsSpan = document.getElementById('custom-dispatch-countdown-minutes-span');
  const secsSpan = document.getElementById('custom-dispatch-countdown-seconds-span');

  let interval;
  
  function updateTimer() {
    const nowUTC = Date.now();

    // 15:00 Madrid = 13:00 UTC
    const now     = new Date(nowUTC);
    const y       = now.getUTCFullYear();
    const m       = now.getUTCMonth();
    const d       = now.getUTCDate();
    const cutoffUTCms = Date.UTC(y, m, d, 13, 0, 0);

    let diff = cutoffUTCms - nowUTC;
    if (diff <= 0) {
      widget.style.display = 'none';
      clearInterval(interval);
      return;
    }

    const hrs  = Math.floor(diff / 3_600_000);
    diff %= 3_600_000;
    const mins = Math.floor(diff / 60_000);
    diff %= 60_000;
    const secs = Math.floor(diff / 1000);

    hrsSpan.textContent  = String(hrs).padStart(2, '0');
    minsSpan.textContent = String(mins).padStart(2, '0');
    secsSpan.textContent = String(secs).padStart(2, '0');
  }

  updateTimer();
  interval = setInterval(updateTimer, 1000);
})();

/* =======================================================================
   Large Volume Quote: lightweight iframe prefill
======================================================================== */

function initLargeVolumeQuoteSkuFill() {
  const BTN_SELECTOR = '#custom-large-volume-quote-btn';
  const CUSTOM_ID    = 'custom-large-volume-quote-product-sku';
  const HUMAN_NAME   = 'Product SKU';
  const FRAME_HINTS  = [
    'form_builder',
    'hulk',
    'cdn.shopify.com/extensions'
  ];

  // Get current SKU from selected variant or visible SKU text
  const resolveSku = () => {
    const variant = getSelectedVariant(optionSelectors);
    const fallback = document.querySelector('.product-sku__value')?.textContent?.trim() || '';
    return (variant?.sku ? String(variant.sku) : fallback || '').trim();
  };

  // Check if an iframe looks like Hulk's popup
  const isHulkIframe = (iframe) => {
    if (!iframe || iframe.tagName !== 'IFRAME') return false;
    const src = iframe.getAttribute('src') || '';
    return FRAME_HINTS.some(h => src.includes(h));
  };

  // Add prefill params to the iframe URL (query + hash)
  const withPrefill = (rawUrl, sku) => {
    const url = new URL(rawUrl, location.href);
    const kv = [
      [`prefill[${CUSTOM_ID}]`, sku],
      [`prefill[${HUMAN_NAME}]`, sku],
      ['prefill[product_sku]', sku],
      ['product_sku', sku],
    ];
    kv.forEach(([k, v]) => url.searchParams.set(k, v));
    const hash = new URLSearchParams(url.hash.replace(/^#/, ''));
    kv.forEach(([k, v]) => hash.set(k, v));
    url.hash = hash.toString() ? `#${hash.toString()}` : '';
    return url.toString();
  };

  // Find/rewrite the first Hulk iframe; observe if not present yet
  const rewriteIframeSrcWhenReady = (sku, timeoutMs = 10000) => {
    const findFrame = () => Array.from(document.querySelectorAll('iframe')).find(isHulkIframe);
    const frameNow = findFrame();
    if (frameNow) {
      frameNow.setAttribute('src', withPrefill(frameNow.getAttribute('src') || '', sku));
      return;
    }

    const start = performance.now();
    const obs = new MutationObserver(() => {
      const f = findFrame();
      if (f) {
        obs.disconnect();
        f.setAttribute('src', withPrefill(f.getAttribute('src') || '', sku));
      } else if (performance.now() - start > timeoutMs) {
        obs.disconnect();
      }
    });
    obs.observe(document.body, { childList: true, subtree: true });
  };

  // Bind once (capture phase so we resolve before popup mounts)
  if (document.documentElement.dataset.lvqIframeBind === '1') return;
  document.documentElement.dataset.lvqIframeBind = '1';

  document.addEventListener('click', (e) => {
    if (!e.target.closest(BTN_SELECTOR)) return;
    const sku = resolveSku();
    if (!sku) return;
    rewriteIframeSrcWhenReady(sku, 12000);
  }, { capture: true });
}

/* =======================================================================
   Custom Tabs Functions
======================================================================== */

/**
 * Initializes custom tab functionalities.
 */
function initCustomTabs() {
  const specButton = document.querySelector('.product-specifications-btn');
  
  if (specButton) {
    specButton.addEventListener('click', () => {
      const tab = document.querySelector('#product-specifications-tab');

      if (tab) {
        tab.setAttribute('open', '');
        tab.click();
        smoothScrollTo(tab, 1500);
      }
    });
  }
}

/* =======================================================================
   Metafields Functions
======================================================================== */

/** Normalize metafield entry (plain or {value}). */
function getMetaValue(entry) {
  return entry && typeof entry === 'object' && 'value' in entry ? entry.value : entry;
}

/** True if value should be shown. */
function hasDisplayValue(v) {
  return !(v == null || v === '' || v === 'No value' || v === 'N' || v === false);
}

/** Format value for display: ✓ for true/"Y", join arrays, else string. */
function renderDisplayValue(v) {
  if (v === true || v === 'Y') {
    return `<svg aria-hidden="true" focusable="false"
      xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"
      width="1em" height="1em" style="vertical-align:-.125em; fill:currentColor;">
      <path d="M173.9 439.4L7 272.5c-9.4-9.4-9.4-24.6 
               0-33.9l40-40c9.4-9.4 24.6-9.4 
               33.9 0l97 97 208-208c9.4-9.4 
               24.6-9.4 33.9 0l40 40c9.4 
               9.4 9.4 24.6 0 33.9L207.9 
               439.4c-9.4 9.4-24.6 9.4-34 
               0z"/></svg>`;
  }
  return Array.isArray(v) ? v.join(', ') : String(v);
}

/** Fill metafield <td>s and hide rows without displayable values. */
function displayVariantMetafieldsForContainers(variant) {
  if (!variant?.metafields) return;
  const meta = variant.metafields;

  for (const key in meta) {
    if (!Object.hasOwn(meta, key)) continue;
    const val = getMetaValue(meta[key]);
    const show = hasDisplayValue(val);
    document.querySelectorAll(`.product-property-value-${key}`).forEach(td => {
      const row = td.closest('tr'); if (!row) return;
      if (show) { td.innerHTML = renderDisplayValue(val); row.classList.remove('custom-hidden'); }
      else row.classList.add('custom-hidden');
    });
  }

  document.querySelectorAll('.product-metafields-container tr').forEach(row => {
    const key = row.className.match(/product-property-([^\s]+)/)?.[1];
    if (!key) return;
    const val = getMetaValue(meta[key]);
    if (!Object.hasOwn(meta, key) || !hasDisplayValue(val)) row.classList.add('custom-hidden');
  });
}

/* =======================================================================
   Cookie Helpers
======================================================================== */

/** Set cookie with name, value, and expiry (days). */
function setCookie(name, value, days) {
  let expires = "";
  if (days) {
    const d = new Date();
    d.setTime(d.getTime() + days * 864e5);
    expires = "; expires=" + d.toUTCString();
  }
  document.cookie = name + "=" + (value || "") + expires + "; path=/";
}

/** Get cookie by name, or null. */
function getCookie(name) {
  const nameEQ = name + "=";
  return document.cookie
    .split(";")
    .map(c => c.trim())
    .find(c => c.startsWith(nameEQ))
    ?.substring(nameEQ.length) || null;
}

/* =======================================================================
   Utility Functions
======================================================================== */

/** Smooth-scroll to target element in given duration (ms). */
function smoothScrollTo(target, duration) {
  const start = window.pageYOffset;
  const sticky = document.querySelector('.header')?.offsetHeight || 0;
  const end = target.getBoundingClientRect().top + start - sticky;
  const dist = end - start;
  const t0 = performance.now();

  const ease = t => t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;

  function step(tNow) {
    const p = Math.min((tNow - t0) / duration, 1);
    window.scrollTo(0, start + dist * ease(p));
    if (p < 1) requestAnimationFrame(step);
  }
  requestAnimationFrame(step);
}